<script setup>
import { ref, watch } from 'vue'
import { useQuasar } from 'quasar'
import { color, use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components'
import VChart from 'vue-echarts'
import apiDashboard from '../../../api/dashboard.api'
import { useI18n } from "vue-i18n"
import {
  useAppI18n,
} from '../../../i18n'

const { t } = useAppI18n()

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
])


const $q = useQuasar()
const loading = ref(false)

const dates = ref([])
const amounts = ref([])
const energy = ref([])
const waitingTime = ref([])
const counts = ref([])

const dateTo = ref(new Date().toISOString().slice(0, 10))
const dateFrom = ref(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10))
const scale = ref(t('dashboard.chart.filter.scale.day'))
const scales = [
  { id: 'H', name: t('dashboard.chart.filter.scale.hour') },
  { id: 'd', name: t('dashboard.chart.filter.scale.day') },
  { id: 'w', name: t('dashboard.chart.filter.scale.week') },
  { id: 'm', name: t('dashboard.chart.filter.scale.month') },
  { id: 'y', name: t('dashboard.chart.filter.scale.year') }
]

const seriesNameToUnitsMap = {}

seriesNameToUnitsMap[t('dashboard.chart.amount')] = ' ₮'
seriesNameToUnitsMap[t('dashboard.chart.energy')] = ' kWh'
seriesNameToUnitsMap[t('dashboard.chart.waitingTime')] = ' min'
seriesNameToUnitsMap[t('dashboard.chart.purchaseCount')] = ''

const textColor = $q.dark.isActive ? '#eee' : '#111'

const option = ref({
  title: {
    text: t('dashboard.chart.purchases'),
    left: 'center',
    textStyle: {
      fontWeight: 'bold',
      color: textColor
    }
  },
  tooltip: {
    trigger: 'axis',
    formatter: (params) => {
      let tooltipText = `${params[0].axisValue}<br/>`

      params.forEach((p) => {
        let unit = seriesNameToUnitsMap[p.seriesName]

        tooltipText += `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
            <span>${p.marker} ${p.seriesName}: </span>
            <span style="padding-left: 40px"><b>${p.value}${unit}</b></span>
          </div>
        `
      })

      return tooltipText
    }
  },
  legend: {
    data: [
      t('dashboard.chart.amount'),
      t('dashboard.chart.energy'),
      t('dashboard.chart.waitingTime'),
      t('dashboard.chart.purchaseCount')
    ],
    top: '8%',
    textStyle: {
      color: textColor,
      fontSize: 14,
      fontWeight: 'bold',
    },
  },
  grid: {
    bottom: '10%',
    top: '15%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    data: dates.value,
    axisLine: { lineStyle: { color: textColor } },
  },
  yAxis: [
    {
      type: 'value',
      name: t('dashboard.chart.amount'),
      position: 'left',
      offset: 0,
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: { formatter: '{value} ₮' },
    },
    {
      type: 'value',
      name: t('dashboard.chart.energy'),
      position: 'left',
      offset: 65,
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: { formatter: '{value} kWh' },
    },
    {
      type: 'value',
      name: t('dashboard.chart.waitingTime'),
      position: 'right',
      offset: 0,
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: { formatter: '{value} min' },
    },
    {
      type: 'value',
      name: t('dashboard.chart.purchaseCount'),
      position: 'right',
      offset: 100,
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: { formatter: '{value}' },
    }
  ],
  series: [
    {
      name: t('dashboard.chart.amount'),
      type: 'line',
      yAxisIndex: 0,
      areaStyle: {},
      data: amounts,
    },
    {
      name: t('dashboard.chart.energy'),
      type: 'line',
      yAxisIndex: 1,
      areaStyle: {},
      data: energy
    },
    {
      name: t('dashboard.chart.waitingTime'),
      type: 'line',
      yAxisIndex: 2,
      areaStyle: {},
      data: waitingTime
    },
    {
      name: t('dashboard.chart.purchaseCount'),
      type: 'line',
      yAxisIndex: 3,
      areaStyle: {},
      data: counts
    }
  ]
})

watch([dateFrom, dateTo, scale], () => {
  applyFilter()
})

function applyFilter() {
  fetchData()
}

async function fetchData() {
  try {
    loading.value = true
    const { data } = await apiDashboard.getChart({
      'date-start': dateFrom.value,
      'date-end': dateTo.value,
      scale: scale.value.id,
    })

    dates.value = data.map(item => item._id)
    counts.value = data.map(item => item.purchaseCount)

    option.value.xAxis.data = dates.value
    option.value.series[3].data = counts.value
  } catch (err) {
    console.error(err)
  } finally {
    loading.value = false
  }
}

fetchData()
</script>

<template lang="pug">
.chart-container.q-pa-md
  form(@submit.prevent='applyFilter' class='form-horizontal')
    q-input(v-model='dateFrom' :label="$t('dashboard.chart.filter.dateFrom')" type='date')
    q-input(v-model='dateTo' :label="$t('dashboard.chart.filter.dateTo')" type='date')
    q-select(v-model='scale' :options='scales' option-value='id' option-label='name' label='Scale' style='width: 100px')
  v-chart(class='chart' :option='option')
</template>

<style scoped>
.chart-container {
  width: 100%;
}

.form-horizontal {
  display: flex;
  gap: 2rem;
  align-items: center;
  margin-bottom: 1rem;
  margin-left: 3rem;
}

.chart {
  width: 110%;
  margin-bottom: -22rem;
  margin-left: -7rem;
}
</style>
